import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  Stethoscope, 
  Baby, 
  Activity,
  Droplets,
  Wind,
  FileCheck,
  Scissors,
  ArrowRight,
  CheckCircle,
  ChevronDown
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { SEO } from '../components/SEO';

import { ToothIcon } from '../components/icons/ToothIcon';
import { ScalpelIcon } from '../components/icons/ScalpelIcon';

const DENTAL_IMAGE = "https://images.unsplash.com/photo-1704455306251-b4634215d98f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBkZW50YWwlMjBjbGluaWMlMjBpbnRlcmlvciUyMGJyaWdodHxlbnwwfHx8fDE3NzAyMTUzNDJ8MA&ixlib=rb-4.1.0&q=85";

// Medical services with detailed descriptions
const medicalServices = [
  { 
    name: 'Adult Consultations', 
    description: 'Comprehensive health assessments for adults',
    details: 'Acute care for respiratory infections, UTIs, gastritis, reflux, skin conditions like acne and more. We include ultrasounds, ECG, and blood tests to diagnose conditions quickly.',
    hasDropdown: true,
    pricingLink: '/prices#adult-consultations'
  },
  { 
    name: 'Paediatric Consultations', 
    description: 'Specialized care for children of all ages',
    details: 'Complete care for children including respiratory infections, ear infections, skin conditions, and childhood illnesses. Investigations included when needed for quick diagnosis.',
    hasDropdown: true,
    pricingLink: '/prices#paediatric-consultations'
  },
  { 
    name: 'Chronic Consultations', 
    description: 'Ongoing management of chronic conditions',
    details: 'Managing hypertension, diabetes, high cholesterol, osteoarthritis, COPD and more. We handle blood tests, medication delivery via Medilogistics, and specialist referrals. Contact us for chronic medication delivery.',
    hasDropdown: true,
    pricingLink: '/prices#chronic-consultations'
  },
  { 
    name: 'HIV Management', 
    description: 'Confidential testing, treatment, and support',
    details: 'Comprehensive HIV care including testing, antiretroviral therapy, viral load monitoring, CD4 counts, and ongoing support. We manage all aspects from blood tests to medication delivery via Medilogistics, and specialist referrals when needed.',
    hasDropdown: true,
    pricingLink: '/prices#hiv-management'
  },
  { 
    name: 'Pregnancy Ultrasounds', 
    description: 'Prenatal monitoring and imaging',
    details: 'Ultrasounds from early gestation to 3rd trimester using high-quality colour doppler technology for detailed prenatal imaging.',
    hasDropdown: true,
    pricingLink: '/prices#pregnancy-ultrasounds'
  },
  { 
    name: 'All Blood Tests', 
    description: 'Comprehensive blood testing',
    details: 'We use only SANAS accredited labs (Lancet & Ampath). Contact us if you need bloods drawn – we\'re happy to help.',
    hasDropdown: true,
    pricingLink: '/prices#blood-tests'
  },
  { 
    name: 'Nebulisation', 
    description: 'Respiratory treatment',
    details: 'Available for anyone who needs respiratory relief.',
    hasDropdown: true,
    pricingLink: '/prices#nebulisation'
  },
  { 
    name: 'ECG', 
    description: 'Heart rhythm monitoring',
    details: 'Done for acute conditions (suspected heart attack, abnormal rhythm), chronic management (hypertension, diabetes), and employment medicals.',
    hasDropdown: true,
    pricingLink: '/prices#ecg'
  },
  { 
    name: 'Allergies and Asthma', 
    description: 'Diagnosis and management',
    details: 'We diagnose and manage Asthma, Hayfever, Allergic Conjunctivitis, Eczema, and Urticaria for adults and children. Specialised blood tests and skin prick allergy tests available.',
    hasDropdown: true,
    pricingLink: '/prices#medical-services'
  },
  { 
    name: 'Acne and Dermatology', 
    description: 'Skin care solutions',
    details: 'Diagnosis and treatment for common skin conditions including acne at affordable rates.',
    hasDropdown: true,
    pricingLink: '/prices#medical-services'
  },
  { 
    name: 'Vitamin Injections', 
    description: 'Energy boost and supplementation',
    hasDropdown: false,
    isLink: true,
    pricingLink: '/prices#vitamin-injections'
  },
  { 
    name: 'IV Drips and Infusions', 
    description: 'Hydration and vitamin therapy',
    hasDropdown: false,
    isLink: true,
    pricingLink: '/prices#vivid-wellness'
  },
  { 
    name: 'Minor Injuries', 
    description: 'Treatment for everyday injuries',
    hasDropdown: false,
    isLink: true,
    pricingLink: '/prices#medical-services'
  },
  { 
    name: 'Female Health', 
    description: 'Women\'s health services',
    hasDropdown: false,
    isLink: true,
    pricingLink: '/prices#medical-services'
  },
];

const surgicalServices = [
  { name: 'Suturing of Wounds', description: 'Professional wound closure and care' },
  { name: 'Chronic Wound Care', description: 'Ongoing wound management and dressing changes' },
  { name: 'Removal of Warts', description: 'Professional wart removal — most procedures take less than 30 minutes' },
  { name: 'Removal of Lipomas (excluding lab fees)', description: 'Safe removal of fatty tissue growths' },
  { name: 'Removal of Cysts (excluding lab fees)', description: 'Expert cyst extraction procedures' },
  { name: 'Removal of Implanon', description: 'Contraceptive implant removal' },
  { name: 'Neonatal Circumcision', description: 'Safe circumcision for newborns' },
  { name: 'Adult Circumcision', description: 'Professional circumcision for adults' },
];

const permitServices = [
  { name: 'PrDP Permits', description: 'Professional Driving Permit medicals' },
  { name: 'Fitness Medicals', description: 'Occupational health assessments' },
  { name: 'Insurance Medicals', description: 'Life and health insurance examinations' },
];

const dentalServices = [
  { name: 'Consultations', description: 'Comprehensive dental examinations' },
  { name: 'Scale and Polish', description: 'Professional teeth cleaning' },
  { name: 'Fillings', description: 'Cavity treatment and restoration' },
  { name: 'Root Canals', description: 'Endodontic treatment for infected teeth' },
  { name: 'Crowns and Bridges', description: 'Tooth restoration and replacement' },
  { name: 'Dentures', description: 'Full and partial denture solutions' },
  { name: 'Panoramic and Conventional X-rays', description: 'Dental imaging and diagnostics' },
  { name: 'Extractions', description: 'Safe and professional tooth removal' },
  { name: 'Cosmetic Dentistry', description: 'Smile enhancement and aesthetic treatments' },
  { name: 'Slimming Wires', description: 'Dental slimming wire treatment' },
  { name: 'Dry Socket Treatment', description: 'Post-extraction complication care' },
  { name: 'Recementation of Crowns', description: 'Crown repair and reattachment' },
  { name: 'Gold and Silver', description: 'Precious metal dental restorations' },
  { name: 'Cosmetic Jewelry', description: 'Dental gems and tooth jewelry' },
  { name: 'Reline/Rebase of Dentures', description: 'Denture adjustment and fitting' },
  { name: 'Retainers', description: 'Orthodontic retention devices' },
  { name: 'Sports Bite Plane', description: 'Athletic mouth protection' },
  { name: 'Teeth Whitening', description: 'Professional teeth brightening' },
];

const ServiceList = ({ services, icon: Icon, color, isMedical = false }) => {
  const [openItems, setOpenItems] = useState({});

  const toggleItem = (name) => {
    setOpenItems(prev => ({ ...prev, [name]: !prev[name] }));
  };

  return (
    <div className="grid gap-3">
      {services.map((service, index) => (
        <div 
          key={service.name}
          className="rounded-xl bg-white border border-slate-100 hover:border-teal-200 transition-all duration-200 animate-fade-in-up overflow-hidden"
          style={{ animationDelay: `${index * 50}ms` }}
          data-testid={`service-item-${service.name.toLowerCase().replace(/\s+/g, '-')}`}
        >
          {/* Service Header */}
          {service.isLink ? (
            <Link 
              to={service.pricingLink}
              className="flex items-center gap-4 p-4 hover:bg-teal-50 transition-colors duration-200"
            >
              <div className={`${color} rounded-lg p-2 flex-shrink-0`}>
                <CheckCircle className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-slate-900">{service.name}</h4>
                <p className="text-slate-600 text-sm">{service.description}</p>
              </div>
              <ArrowRight className="w-5 h-5 text-teal-600" />
            </Link>
          ) : service.hasDropdown ? (
            <>
              <button
                onClick={() => toggleItem(service.name)}
                className="w-full flex items-center gap-4 p-4 hover:bg-slate-50 transition-colors duration-200"
              >
                <div className={`${color} rounded-lg p-2 flex-shrink-0`}>
                  <CheckCircle className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1 text-left">
                  <h4 className="font-semibold text-slate-900">{service.name}</h4>
                  <p className="text-slate-600 text-sm">{service.description}</p>
                </div>
                <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform duration-200 ${openItems[service.name] ? 'rotate-180' : ''}`} />
              </button>
              {openItems[service.name] && (
                <div className="px-4 pb-4 pt-0 border-t border-slate-100 bg-slate-50">
                  <p className="text-slate-600 text-sm py-3">{service.details}</p>
                  <Link 
                    to={service.pricingLink}
                    className="inline-flex items-center gap-1 text-teal-600 hover:text-teal-700 text-sm font-medium"
                  >
                    View pricing <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              )}
            </>
          ) : (
            <div className="flex items-center gap-4 p-4">
              <div className={`${color} rounded-lg p-2 flex-shrink-0`}>
                <CheckCircle className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-slate-900">{service.name}</h4>
                <p className="text-slate-600 text-sm">{service.description}</p>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export const Services = () => {
  const location = useLocation();
  const validTabs = ['medical', 'surgical', 'permits', 'dental'];
  const hashTab = location.hash.replace('#', '');
  const [activeTab, setActiveTab] = useState(validTabs.includes(hashTab) ? hashTab : 'medical');

  useEffect(() => {
    const tab = location.hash.replace('#', '');
    if (validTabs.includes(tab)) {
      setActiveTab(tab);
      setTimeout(() => {
        document.getElementById('services-tabs-section')?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, [location.hash]);

  return (
    <div className="min-h-screen" data-testid="services-page">
      <SEO 
        title="Services"
        description="Medical and dental services at K&K Medical and Dental. GP consultations, dental care, surgical procedures, HIV management, pregnancy ultrasounds, ECG, and medical certificates in Umzinto."
        keywords="medical services Umzinto, dental services KZN, GP consultation, root canal, dentures, ECG, pregnancy ultrasound, HIV management, PrDP permit, fitness medical"
      />
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-slate-50 to-teal-50/30 py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in-up">
              <span className="inline-block px-4 py-2 rounded-full bg-teal-100 text-teal-700 text-sm font-semibold mb-6">
                Our Services
              </span>
              <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-slate-900 mb-6">
                Comprehensive Healthcare Under One Roof
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed mb-8">
                From general medical consultations to specialized dental care, we provide a full range of services to meet all your healthcare needs.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  asChild
                  className="bg-teal-600 hover:bg-teal-700 text-white rounded-full px-8 py-6 shadow-lg hover:shadow-xl transition-all duration-300"
                  data-testid="services-book-btn"
                >
                  <Link to="/booking">Book Appointment</Link>
                </Button>
                <Button 
                  asChild
                  variant="outline"
                  className="border-2 border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white rounded-full px-8 py-6 transition-all duration-300"
                  data-testid="services-prices-btn"
                >
                  <Link to="/prices">View Prices</Link>
                </Button>
              </div>
            </div>
            <div className="relative animate-fade-in-up animation-delay-200">
              <div className="rounded-3xl overflow-hidden shadow-2xl">
                <video 
                  src="https://customer-assets.emergentagent.com/job_umzinto-doc-preview/artifacts/3xi3cubi_5721605-hd_2048_1080_25fps.mp4" 
                  autoPlay 
                  muted 
                  loop 
                  playsInline
                  className="w-full h-[400px] object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Tabs Section */}
      <section id="services-tabs-section" className="py-16 lg:py-24 bg-slate-50" data-testid="services-tabs-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-xs font-semibold uppercase tracking-widest text-teal-600 mb-3">What We Offer</p>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mb-4">
              Explore Our Services
            </h2>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2 lg:grid-cols-4 gap-2 bg-transparent h-auto p-0 mb-8" data-testid="services-tabs">
              <TabsTrigger 
                value="medical" 
                className="bg-white border-2 border-transparent data-[state=active]:border-teal-500 data-[state=active]:bg-teal-50 rounded-xl py-4 px-4 font-semibold transition-all duration-200"
                data-testid="tab-medical"
              >
                <Stethoscope className="w-5 h-5 mr-2" />
                Medical
              </TabsTrigger>
              <TabsTrigger 
                value="surgical" 
                className="bg-white border-2 border-transparent data-[state=active]:border-purple-500 data-[state=active]:bg-purple-50 rounded-xl py-4 px-4 font-semibold transition-all duration-200"
                data-testid="tab-surgical"
              >
                <ScalpelIcon className="w-5 h-5 mr-2" />
                Surgical
              </TabsTrigger>
              <TabsTrigger 
                value="permits" 
                className="bg-white border-2 border-transparent data-[state=active]:border-amber-500 data-[state=active]:bg-amber-50 rounded-xl py-4 px-4 font-semibold transition-all duration-200"
                data-testid="tab-permits"
              >
                <FileCheck className="w-5 h-5 mr-2" />
                Insurance
              </TabsTrigger>
              <TabsTrigger 
                value="dental" 
                className="bg-white border-2 border-transparent data-[state=active]:border-blue-500 data-[state=active]:bg-blue-50 rounded-xl py-4 px-4 font-semibold transition-all duration-200"
                data-testid="tab-dental"
              >
                <ToothIcon className="w-5 h-5 mr-2" />
                Dental
              </TabsTrigger>
            </TabsList>

            <TabsContent value="medical" className="mt-0" id="medical" data-testid="tab-content-medical">
              <Card className="border-0 shadow-lg rounded-2xl">
                <CardHeader className="pb-4">
                  <div className="flex items-center gap-4">
                    <div className="bg-teal-500 rounded-xl p-3">
                      <Stethoscope className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <CardTitle className="text-2xl">Medical Services</CardTitle>
                      <p className="text-slate-600">Adult and Paediatric healthcare</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <ServiceList services={medicalServices} icon={Stethoscope} color="bg-teal-500" />
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="surgical" className="mt-0" id="surgical" data-testid="tab-content-surgical">
              <Card className="border-0 shadow-lg rounded-2xl">
                <CardHeader className="pb-4">
                  <div className="flex items-center gap-4">
                    <div className="bg-purple-500 rounded-xl p-3">
                      <ScalpelIcon className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <CardTitle className="text-2xl">Surgical Services</CardTitle>
                      <p className="text-slate-600">Minor surgical procedures</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <ServiceList services={surgicalServices} icon={Scissors} color="bg-purple-500" />
                  <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                    <p className="text-amber-800 font-medium text-sm">
                      <span className="font-bold">Note:</span> Surgical procedures require an appointment. 
                      Please contact us to schedule.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="permits" className="mt-0" id="permits" data-testid="tab-content-permits">
              <Card className="border-0 shadow-lg rounded-2xl">
                <CardHeader className="pb-4">
                  <div className="flex items-center gap-4">
                    <div className="bg-amber-500 rounded-xl p-3">
                      <FileCheck className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <CardTitle className="text-2xl">Insurance & Medicals</CardTitle>
                      <p className="text-slate-600">Certification and compliance examinations</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <ServiceList services={permitServices} icon={FileCheck} color="bg-amber-500" />
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="dental" className="mt-0" id="dental" data-testid="tab-content-dental">
              <Card className="border-0 shadow-lg rounded-2xl">
                <CardHeader className="pb-4">
                  <div className="flex items-center gap-4">
                    <div className="bg-blue-500 rounded-xl p-3">
                      <ToothIcon className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <CardTitle className="text-2xl">Dental Services</CardTitle>
                      <p className="text-slate-600">Complete dental care solutions</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <ServiceList services={dentalServices} icon={Scissors} color="bg-blue-500" />
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 lg:py-24 bg-white" data-testid="services-cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-teal-600 to-teal-700 rounded-3xl p-8 lg:p-12 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Ready to Book Your Visit?
            </h2>
            <p className="text-teal-100 text-lg mb-8 max-w-2xl mx-auto">
              Walk-in consultations are welcome. For surgical procedures, please book an appointment.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Button 
                asChild
                className="bg-white text-teal-700 hover:bg-slate-100 rounded-full px-8 py-6 text-lg shadow-lg hover:shadow-xl transition-all duration-300"
                data-testid="services-cta-contact-btn"
              >
                <Link to="/contact">Contact Us</Link>
              </Button>
              <Button 
                asChild
                variant="outline"
                className="border-2 border-white text-white hover:bg-white/10 rounded-full px-8 py-6 text-lg transition-all duration-300"
                data-testid="services-cta-prices-btn"
              >
                <Link to="/prices">View Prices</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
